import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import AttentionReasonChips from '@/components/FollowUp/AttentionReasonChips';
import { parseTimeFrame } from '@/utils/dater';

/**
 * NeedsAttentionCard — ONE card per person in the Overview Needs Attention
 * section (spec §6.1: multiple reasons grouped on one card, never duplicate
 * person cards). Reason rows carry their own audience scope and snooze
 * state; `firstDetectedAt` shows when the earliest flag was raised.
 */
const NeedsAttentionCard = ({ personID, name, profile, reasons = [], onPress }) => {
    const activeReasons = reasons.filter((r) => !r?.snoozed);
    const earliest = reasons
        .map((r) => r?.firstDetectedAt)
        .filter(Boolean)
        .sort()[0];

    return (
        <Pressable
            testID={`needs-attention-${personID}`}
            accessibilityRole="button"
            onPress={onPress}
            style={styles.card}
        >
            <Avatar size={36} profile={{ id: personID, firstName: profile?.firstName, lastName: profile?.lastName, avatarStorageKey: profile?.avatarStorageKey, avatarUpdatedAt: profile?.avatarUpdatedAt }} onPress={onPress} />
            <View style={styles.body}>
                <View style={styles.titleLine}>
                    <Text style={styles.name} numberOfLines={1}>{name || 'Unknown'}</Text>
                    {!!earliest && (
                        <Text style={styles.since}>since {parseTimeFrame(earliest)}</Text>
                    )}
                </View>
                <AttentionReasonChips reasons={reasons} testIDPrefix={`attention-${personID}`} />
                {activeReasons.length === 0 && reasons.length > 0 && (
                    <Text style={styles.allSnoozed}>All reasons snoozed — still monitored.</Text>
                )}
            </View>
            <Unicon name="chevron-forward-outline" size={18} colorVariant="textTertiary" />
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    card: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        marginBottom: 8,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.warningContainer,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    body: { flex: 1, minWidth: 0, gap: 6 },
    titleLine: { flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
    name: { fontSize: 15, fontFamily: fonts.heading, color: colors.text },
    since: { fontSize: 11, color: colors.textSecondary },
    allSnoozed: { fontSize: 11, color: colors.textTertiary, fontStyle: 'italic' },
}));

export default NeedsAttentionCard;
